import React, { useMemo } from 'react';

// Utils
import { balanceFormatter } from '~/utils/helperFunctions';
import { COMPAIGNS_ACTION_TYPE } from '~/utils/constants';

// MUI Components
import { Box, Dialog, DialogTitle, Tooltip, Typography } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';

// Styles
import './styles.scss';
import useScreenWidth from '~/hooks/useScreenWidth';

const CampaignCostBreakdown = ({ costDetails, open, onClose, template }) => {
  // Compute once per render at top-level to avoid calling hooks inside memo callbacks
  const isWideScreen = useScreenWidth(1200);
  const toNumber = (value) => Number(value || 0);
  const getAddOns = (component) => {
    const additionalPageCost = component.hasAdditionalPage
      ? toNumber(component.perMailerAdditionalPageCost)
      : 0;
    const uvCoatingCost = component.hasUvCoating ? toNumber(component.perMailerUvCoatingCost) : 0;

    const total =
      (component.hasGsv ? toNumber(component.perMailerGsvCost) : 0) +
      (component.hasRos ? toNumber(component.perMailerRosCost) : 0) +
      additionalPageCost +
      uvCoatingCost;

    return total > 0 ? balanceFormatter(total) : '-';
  };

  const columns = useMemo(
    () => [
      {
        field: 'template',
        headerName: 'Template',
        flex: isWideScreen ? 2 : 0,
        width: 200,
        renderCell: (params) => {
          const isSingleMailer = params?.row?.type === 'single' && template;

          const templateText = isSingleMailer
            ? template?.title || '-'
            : 'ID:' + params?.row?.templateId + ' ' + (params?.row?.template?.title || '') || '-';

          return (
            <Tooltip title={templateText} placement="top-start">
              <Typography className="templateNameCell">{templateText}</Typography>
            </Tooltip>
          );
        },
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      },
      {
        field: 'mailer',
        headerName: 'Product',
        flex: isWideScreen ? 1 : 0,
        width: 200,
        renderCell: (params) => {
          const isSingleMailer = params?.row?.type === 'single' && template;

          const mailerText = isSingleMailer
            ? template?.object?.product?.productType || '-'
            : COMPAIGNS_ACTION_TYPE.find(
                (product) => +product?.productId === +params?.row?.product?.id
              )?.label ||
              params?.row.product?.productType ||
              '-';

          return (
            <Tooltip title={mailerText} placement="top-start">
              <Typography className="templateNameCell">{mailerText}</Typography>
            </Tooltip>
          );
        },
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      },
      {
        field: 'qty',
        headerName: 'QTY',
        flex: isWideScreen ? 1 : 0,
        width: 100,
        valueGetter: (params) => params.row.deliverableContacts,
        sortable: false,
      },
      {
        field: 'perMailer',
        headerName: 'Per Mailer',
        flex: isWideScreen ? 1 : 0,
        width: 100,
        valueGetter: (params) => balanceFormatter(params.row.perMailerCost, 3),
        sortable: false,
      },
      {
        field: 'addOns',
        headerName: 'Add Ons',
        flex: isWideScreen ? 1 : 0,
        width: 100,
        valueGetter: (params) => getAddOns(params.row),
        sortable: false,
      },
      {
        field: 'totalCost',
        headerName: 'Total Cost',
        flex: isWideScreen ? 1 : 0,
        width: 100,
        valueGetter: (params) => balanceFormatter(params.row.cost, 3),
        sortable: false,
      },
    ],
    [isWideScreen]
  );

  // Filter and prepare data for DataGrid
  const breakdown = Array.isArray(costDetails?.breakdown) ? costDetails.breakdown : [];
  const rows = useMemo(() => {
    return breakdown
      .filter((item) => item.type !== 'delay')
      .map((component, index) => ({
        ...component,
        id: component.id || index, // Ensure each row has an id
      }));
  }, [breakdown]);

  // Calculate total
  const totalCost = useMemo(() => {
    return breakdown
      .filter((item) => item.cost)
      .reduce((sum, item) => sum + parseFloat(item.cost), 0);
  }, [breakdown]);

  return (
    <Dialog open={open} onClose={onClose} className="campaignsCostBreakdownDialog">
      <DialogTitle className="campaignsCostBreakdownDialogTitle">
        <Typography variant="h4">Cost Breakdown</Typography>
        <Typography variant="body1">
          Total: <span className="boldNum costNum">{balanceFormatter(totalCost)}</span>
        </Typography>
      </DialogTitle>
      <Box className="campaignsCostBreakdownContainer" sx={{ px: 2 }}>
        {/* DataGrid */}
        <Box className="tableContainer">
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            hideFooter
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            getRowId={(row) => row?.id}
            className="campaignsCostBreakdownTable"
            sx={{
              maxHeight: '500px',
              border: 'none',
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default CampaignCostBreakdown;
